import React, { useState, useRef } from "react";
import { createPortal } from "react-dom";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import Papa from "papaparse";
import api from "../utils/api";

// File types the importer understands
const SPREADSHEET_EXTENSIONS = [".xlsx", ".xls", ".ods"];
const TEXT_EXTENSIONS = [".csv", ".tsv", ".txt"];
const ACCEPTED_EXTENSIONS = [...TEXT_EXTENSIONS, ...SPREADSHEET_EXTENSIONS];

const extensionOf = (name = "") => {
  const dot = name.lastIndexOf(".");
  return dot === -1 ? "" : name.slice(dot).toLowerCase();
};

const isSupportedFile = (file) => ACCEPTED_EXTENSIONS.includes(extensionOf(file?.name));

const TEMPLATE_HEADERS = [
  "productName",
  "category",
  "price",
  "type",
  "unit",
  "stock",
  "description",
  "image",
  "variants",
  "addOns",
];

const TEMPLATE_INSTRUCTIONS = [
  "IMPORTANT: Keep the header row exactly as shown",
  "Required fields: productName, category, price",
  "Optional fields: type, unit, stock, description, image, variants, addOns",
  "Variants & AddOns Format: Name:Price | Name:Price (e.g. Small:99 | Large:149)",
  "If product already exists, its info will be updated.",
  "If category does not exist, it will be created automatically.",
  "price must be a number greater than 0",
];

const TEMPLATE_ROWS = [
  ["Veg Burger", "Burgers", 140, "Veg", "pieces", 100, "Grilled vegetable patty", "https://example.com/vegburger.jpg", "Small:140 | Large:190", "Extra Cheese:20 | Extra Patty:50"],
  ["Chicken Burger", "Burgers", 170, "Non-Veg", "pieces", 50, "Juicy chicken burger", "", "", ""],
  ["Coca Cola", "Drinks", 40, "Veg", "milliliters", 1000, "Regular cold drink", "", "", ""],
];

// Accept "Product Name", "product_name", "PRODUCTNAME" etc. for each template column
const canonicalHeader = (header) => {
  const key = String(header).toLowerCase().replace(/[\s_-]/g, "");
  return TEMPLATE_HEADERS.find((h) => h.toLowerCase() === key) || String(header).trim();
};

// Every value as a trimmed-safe string, the shape validateCSVData expects
const normalizeRow = (row) => {
  const out = {};
  Object.entries(row).forEach(([key, value]) => {
    out[canonicalHeader(key)] = value === null || value === undefined ? "" : String(value);
  });
  return out;
};

const isBlankOrCommentRow = (row) => {
  const values = Object.values(row).map((v) => v.trim());
  return values.every((v) => v === "") || values[0]?.startsWith("#");
};

const readTextRows = (file) =>
  new Promise((resolve, reject) => {
    Papa.parse(file, {
      header: true,
      skipEmptyLines: true,
      comments: "#",
      // Auto-detects comma, tab or semicolon separated files
      delimitersToGuess: [",", "\t", ";", "|"],
      complete: (results) => resolve(results.data),
      error: reject,
    });
  });

const readSpreadsheetRows = async (file) => {
  // Loaded only when a spreadsheet is imported, keeps the main bundle small
  const XLSX = await import("xlsx");
  const workbook = XLSX.read(await file.arrayBuffer());
  const sheetName =
    workbook.SheetNames.find((n) => n.toLowerCase() === "products") || workbook.SheetNames[0];
  // raw values so numbers like 1,200 aren't turned into formatted text
  return XLSX.utils.sheet_to_json(workbook.Sheets[sheetName], { defval: "", raw: true });
};

const readImportRows = async (file) => {
  const rows = SPREADSHEET_EXTENSIONS.includes(extensionOf(file.name))
    ? await readSpreadsheetRows(file)
    : await readTextRows(file);
  return rows.map(normalizeRow).filter((row) => !isBlankOrCommentRow(row));
};

const BulkProductImport = ({ userId, onImportComplete, refreshProducts }) => {
  const [showImportModal, setShowImportModal] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importStats, setImportStats] = useState({
    total: 0,
    added: 0,
    updated: 0,
    skipped: 0,
    failed: 0,
  });
  const [validationErrors, setValidationErrors] = useState([]);
  // const [showInstructions, setShowInstructions] = useState(true);
  const fileInputRef = useRef(null);

  // Download the sample template as CSV or Excel
  const downloadTemplate = async (format = "csv") => {
    if (format === "xlsx") {
      const XLSX = await import("xlsx");
      const workbook = XLSX.utils.book_new();
      const products = XLSX.utils.aoa_to_sheet([TEMPLATE_HEADERS, ...TEMPLATE_ROWS]);
      products["!cols"] = TEMPLATE_HEADERS.map((h) => ({ wch: h === "description" || h === "image" ? 30 : 16 }));
      XLSX.utils.book_append_sheet(workbook, products, "Products");
      XLSX.utils.book_append_sheet(
        workbook,
        XLSX.utils.aoa_to_sheet([["Instructions"], ...TEMPLATE_INSTRUCTIONS.map((line) => [line])]),
        "Instructions",
      );
      XLSX.writeFile(workbook, "bulk_product_template.xlsx");
      toast.success("Excel template downloaded! Fill the Products sheet.");
      return;
    }

    const escape = (value) => {
      const text = String(value);
      return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
    };
    const csvContent = [
      ...TEMPLATE_INSTRUCTIONS.map((line) => `# ${line}`),
      "# ==============================================",
      TEMPLATE_HEADERS.join(","),
      ...TEMPLATE_ROWS.map((row) => row.map(escape).join(",")),
    ].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "bulk_product_template.csv";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);

    toast.success("Template downloaded! Fill it with your products.");
  };

  // Drag and Drop Handlers
  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      const file = files[0];
      if (isSupportedFile(file)) {
        validateAndSetFile(file);
      } else {
        toast.error("Please upload a CSV or Excel file (.csv, .xlsx, .xls)");
      }
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      validateAndSetFile(file);
    }
  };

  const validateAndSetFile = (file) => {
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      toast.error("File too large! Maximum size is 5MB");
      return;
    }

    if (!isSupportedFile(file)) {
      toast.error("Please upload a CSV or Excel file (.csv, .xlsx, .xls, .ods, .tsv)");
      return;
    }

    setCsvFile(file);
    resetErrorDisplay();
  };

  const removeFile = () => {
    setCsvFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    resetErrorDisplay();
  };

  const resetErrorDisplay = () => {
    setValidationErrors([]);
    setImportStats({
      total: 0,
      added: 0,
      updated: 0,
      skipped: 0,
      failed: 0,
    });
  };

  // Get existing categories
  const getExistingCategories = async () => {
    try {
      const response = await api.get(`newcategories/${userId}?limit=1000`);
      return response.data.data.map((cat) => cat.newTitle);
    } catch (error) {
      console.error("Error fetching categories:", error);
      return [];
    }
  };

  const validateCSVData = (data) => {
    const errors = [];
    const validProducts = [];

    data.forEach((row, index) => {
      const rowNum = index + 2;

      if (!row.productName?.trim()) {
        errors.push(`Row ${rowNum}: Product name is required`);
        return;
      }

      if (!row.category?.trim()) {
        errors.push(`Row ${rowNum}: Category is required`);
        return;
      }

      const price = Number(row.price);
      if (!price || price <= 0) {
        errors.push(`Row ${rowNum}: Invalid price for product "${row.productName}"`);
        return;
      }

      if (row.type && !["veg", "non-veg"].includes(row.type.toLowerCase().trim())) {
        errors.push(`Row ${rowNum}: Invalid type "${row.type}". Allowed: Veg, Non-Veg`);
        return;
      }

      const validUnits = [
        "plates", "plate", 
        "pieces", "piece", 
        "grams", "gram", "g",
        "kilograms", "kilogram", "kg", 
        "milligrams", "milligram", "mg", 
        "pounds", "pound", "lb", "lbs", 
        "litres", "litre", "liters", "liter", "l",
        "milliliters", "milliliter", "ml", 
        "dozens", "dozen", 
        "cups", "cup", 
        "glasses", "glass", 
        "bowls", "bowl", 
        "bottles", "bottle", 
        "cans", "can", 
        "boxes", "box", 
        "cartons", "carton"
      ];
      if (row.unit && !validUnits.includes(row.unit.toLowerCase().trim())) {
        errors.push(`Row ${rowNum}: Invalid unit "${row.unit}".`);
        return;
      }

      if (row.stock && (isNaN(Number(row.stock)) || Number(row.stock) < 0)) {
        errors.push(`Row ${rowNum}: Invalid stock value`);
        return;
      }

      // Parse Variants and AddOns
      const parseOptions = (fieldValue) => {
        if (!fieldValue || typeof fieldValue !== 'string') return [];
        return fieldValue.split('|').map(item => {
          const parts = item.split(':');
          if (parts.length >= 2) {
            const name = parts[0].trim();
            const val = Number(parts[1].trim());
            if (name && !isNaN(val)) {
              return { name, price: val, isAvailable: true };
            }
          }
          return null;
        }).filter(Boolean);
      };

      const variants = parseOptions(row.variants);
      const addOns = parseOptions(row.addOns);

      validProducts.push({
        productName: row.productName.trim(),
        category: row.category.trim(),
        type: row.type ? (row.type.toLowerCase().trim() === "non-veg" ? "Non-Veg" : "Veg") : "Veg",
        unit: row.unit ? row.unit.toLowerCase().trim() : "pieces",
        stock: row.stock ? Number(row.stock) : 0,
        price,
        description: row.description ? row.description.trim() : "",
        image: row.image ? row.image.trim() : "",
        variants,
        addOns,
      });
    });

    return { errors, validProducts };
  };

  // Process import (CSV, TSV, Excel or ODS)
  const processCSVImport = async () => {
    if (!csvFile || !userId) {
      toast.error("Please select a CSV or Excel file");
      return;
    }

    setIsImporting(true);
    setImportProgress(30);

    let rows;
    try {
      rows = await readImportRows(csvFile);
    } catch (error) {
      console.error("File parsing error:", error);
      toast.error("Could not read this file. Please check it is a valid CSV or Excel file.");
      setValidationErrors([`File parsing error: ${error.message}`]);
      setIsImporting(false);
      return;
    }

    try {
      const { errors, validProducts } = validateCSVData(rows);

      if (errors.length > 0) {
        setValidationErrors(errors);
        return;
      }

      if (validProducts.length === 0) {
        toast.error("No valid products found in the file");
        return;
      }

      setImportProgress(70);

      const response = await api.post(`bulk-products/${userId}`, {
        products: validProducts,
      });

      const stats = response.data.stats || response.data;

      setImportStats({
        total: stats.total || validProducts.length,
        added: stats.added || 0,
        updated: stats.updated || 0,
        skipped: stats.skipped || 0,
        failed: stats.failed || 0,
      });

      setImportProgress(100);

      if (stats.failed === 0) {
        toast.success(
          `Import complete! Added: ${stats.added}, Updated: ${stats.updated}`,
        );
        setTimeout(handleCloseModal, 2000);
      } else {
        toast.warning(
          `Partial import. Added: ${stats.added}, Updated: ${stats.updated}, Failed: ${stats.failed}`,
        );
      }

      refreshProducts?.();
      onImportComplete?.(stats);
    } catch (error) {
      console.error("Import error:", error);
      toast.error(error.response?.data?.message || "Import failed");
      setValidationErrors([`Processing error: ${error.response?.data?.message || error.message}`]);
    } finally {
      setIsImporting(false);
    }
  };

  const handleCloseModal = () => {
    setShowImportModal(false);
    setCsvFile(null);
    resetErrorDisplay();
    setImportProgress(0);
    setIsImporting(false);
  };

  // Import Modal
  const ImportModal = () => {
    if (!showImportModal) return null;

    return createPortal(
      <div className="fixed inset-0 bg-slate-900/65 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden">
          {/* Modal Header */}
          <div className="bg-teal-600 text-white p-6">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-2xl font-bold">Bulk Product Import</h2>
                <p className="mt-1 text-teal-100 text-sm">
                  Upload a CSV or Excel file to add/update products
                </p>
              </div>
              <button
                onClick={handleCloseModal}
                className="text-white hover:text-gray-200 text-2xl"
                disabled={isImporting}
              >
                &times;
              </button>
            </div>
          </div>

          {/* Modal Body */}
          <div className="p-6 overflow-y-auto max-h-[60vh]">
            {/* Import Stats */}
            {importStats.total > 0 && (
              <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">
                  Import Results
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                  <div className="text-center">
                    <div className="text-2xl font-bold text-gray-700">
                      {importStats.total}
                    </div>
                    <div className="text-sm text-gray-600">Total</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-green-600">
                      {importStats.added}
                    </div>
                    <div className="text-sm text-gray-600">Added</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                      {importStats.updated}
                    </div>
                    <div className="text-sm text-gray-600">Updated</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-yellow-600">
                      {importStats.skipped}
                    </div>
                    <div className="text-sm text-gray-600">Skipped</div>
                  </div>
                  <div className="text-center">
                    <div className="text-2xl font-bold text-red-600">
                      {importStats.failed}
                    </div>
                    <div className="text-sm text-gray-600">Failed</div>
                  </div>
                </div>
              </div>
            )}

            {/* Progress Bar */}
            {isImporting && (
              <div className="mb-6">
                <div className="flex justify-between text-sm text-gray-600 mb-2">
                  <span>Importing products...</span>
                  <span>{importProgress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-2.5">
                  <div
                    className="bg-teal-600 h-2.5 rounded-full transition-all duration-300"
                    style={{ width: `${importProgress}%` }}
                  ></div>
                </div>
              </div>
            )}

            {/* Drag & Drop Area */}
            <div
              className={`border-2 border-dashed rounded-xl p-8 text-center transition-all duration-300 mb-6 ${
                isDragging
                  ? "border-teal-500 bg-teal-50"
                  : csvFile
                    ? "border-green-500 bg-green-50"
                    : "border-gray-300 hover:border-teal-400 hover:bg-gray-50"
              }`}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDragOver={handleDragOver}
              onDrop={handleDrop}
            >
              {csvFile ? (
                <div className="space-y-4">
                  <div className="flex items-center justify-center gap-4">
                    <div className="bg-green-100 p-3 rounded-full">
                      <svg
                        className="w-8 h-8 text-green-600"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                        ></path>
                      </svg>
                    </div>
                    <div className="text-left">
                      <p className="font-medium text-gray-800">
                        {csvFile.name}
                      </p>
                      <p className="text-sm text-gray-600">
                        {(csvFile.size / 1024).toFixed(1)} KB
                      </p>
                    </div>
                  </div>
                  <div className="flex justify-center gap-3">
                    <button
                      onClick={removeFile}
                      className="text-sm text-red-600 hover:text-red-800 font-medium px-4 py-2 border border-red-200 rounded-lg hover:bg-red-50"
                    >
                      Remove File
                    </button>
                    <button
                      onClick={() => fileInputRef.current?.click()}
                      className="text-sm text-teal-600 hover:text-teal-800 font-medium px-4 py-2 border border-teal-200 rounded-lg hover:bg-teal-50"
                    >
                      Change File
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <svg
                    className="w-16 h-16 mx-auto text-gray-400 mb-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
                    ></path>
                  </svg>
                  <p className="text-lg font-medium text-gray-700 mb-2">
                    Drag & drop your CSV or Excel file here
                  </p>
                  <p className="text-gray-500 mb-4">or</p>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="bg-teal-600 hover:bg-teal-700 text-white font-medium py-2.5 px-8 rounded-lg transition duration-300 shadow-md"
                  >
                    Browse Files
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept={ACCEPTED_EXTENSIONS.join(",")}
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <p className="text-sm text-gray-500 mt-4">
                    Supports CSV, Excel (.xlsx, .xls), ODS and TSV files up to 5MB
                  </p>
                </>
              )}
            </div>

            {/* Error Display Section */}
            {validationErrors.length > 0 ? (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-red-800 flex items-center gap-2">
                    <svg
                      className="w-5 h-5"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                    Validation Errors ({validationErrors.length})
                  </h3>
                  <button
                    onClick={resetErrorDisplay}
                    className="text-sm text-teal-600 hover:text-teal-800 font-medium"
                  >
                    Clear Errors
                  </button>
                </div>
                <div className="max-h-48 overflow-y-auto">
                  <ul className="text-sm text-red-700 space-y-2">
                    {validationErrors.slice(0, 2).map((error, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <span className="mt-1">•</span>
                        <span>{error}</span>
                      </li>
                    ))}
                    {validationErrors.length > 2 && (
                      <li className="text-gray-600 italic">
                        ...and {validationErrors.length - 2} more errors
                      </li>
                    )}
                  </ul>
                </div>
                <div className="mt-4 pt-3 border-t border-red-200">
                  <p className="text-sm text-red-600">
                    Please fix these errors and upload again.
                  </p>
                </div>
              </div>
            ) : (
              /* Instructions Section */
              <div className="mb-6 bg-blue-50 border border-blue-200 rounded-lg p-4">
                <h3 className="text-lg font-semibold text-blue-800 mb-3 flex items-center gap-2">
                  <svg
                    className="w-5 h-5"
                    fill="currentColor"
                    viewBox="0 0 20 20"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      fillRule="evenodd"
                      d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                      clipRule="evenodd"
                    ></path>
                  </svg>
                  Import Instructions
                </h3>
                <ul className="text-sm text-blue-700 space-y-2">
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Duplicate handling:</strong> If a product with the same name and category exists, its information (price, etc.) will be updated.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Auto Category Creation:</strong> If the category provided is not found, it will be created automatically.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Required fields:</strong> productName, category, price
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Optional fields:</strong> type, unit, stock, description, image, variants, addOns
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Variants & AddOns Format:</strong> Name:Price | Name:Price (e.g. Small:99 | Large:149)
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-0.5">•</span>
                    <span>
                      <strong>Price must be a positive number</strong> (e.g.,
                      299, 39.99)
                    </span>
                  </li>
                </ul>
              </div>
            )}

            {/* Download Template Section */}
            <div className="text-center">
              <p className="text-gray-700 text-sm font-medium mb-2">Download sample template</p>
              <div className="inline-flex gap-3">
                <button
                  onClick={() => downloadTemplate("csv")}
                  className="px-4 py-2 rounded-lg border border-blue-200 text-blue-700 hover:bg-blue-50 text-sm font-semibold transition"
                >
                  📄 CSV
                </button>
                <button
                  onClick={() => downloadTemplate("xlsx")}
                  className="px-4 py-2 rounded-lg border border-green-200 text-green-700 hover:bg-green-50 text-sm font-semibold transition"
                >
                  📊 Excel (.xlsx)
                </button>
              </div>
              <p className="text-gray-600 text-sm mt-2">
                Fill this template with your products
              </p>
            </div>
          </div>

          {/* Modal Footer */}
          <div className="bg-gray-50 p-6 border-t border-gray-200">
            <div className="flex justify-between items-center">
              <div className="text-sm text-gray-600">
                {csvFile ? `Selected: ${csvFile.name}` : "No file selected"}
              </div>
              <div className="flex gap-3">
                <button
                  onClick={handleCloseModal}
                  className="px-5 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition duration-300 font-medium"
                  disabled={isImporting}
                >
                  Cancel
                </button>
                <button
                  onClick={processCSVImport}
                  disabled={!csvFile || isImporting}
                  className={`px-6 py-2.5 rounded-lg transition duration-300 font-medium ${
                    !csvFile || isImporting
                      ? "bg-gray-300 text-gray-500 cursor-not-allowed"
                      : "bg-teal-600 hover:bg-teal-700 text-white shadow-md"
                  }`}
                >
                  {isImporting ? (
                    <span className="flex items-center gap-2">
                      <svg
                        className="animate-spin h-4 w-4 text-white"
                        xmlns="http://www.w3.org/2000/svg"
                        fill="none"
                        viewBox="0 0 24 24"
                      >
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                        ></circle>
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        ></path>
                      </svg>
                      Importing...
                    </span>
                  ) : (
                    "Start Import"
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>,
      document.body
    );
  };

  return (
    <div className="flex flex-col items-start gap-1">
      {/* Button to open modal */}
      <button
        onClick={() => setShowImportModal(true)}
        className="flex items-center gap-2 bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold px-4 py-2.5 rounded-xl shadow-sm hover:shadow transition-all text-sm"
      >
        <svg
          className="w-4 h-4 text-slate-500"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth="2"
            d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M9 19l3 3m0 0l3-3m-3 3V10"
          ></path>
        </svg>
        Import Bulk Products
      </button>

      <div className="flex items-center gap-1 text-[11px] font-bold pl-1 text-gray-500">
        <span>📥 Sample:</span>
        <button
          onClick={() => downloadTemplate("csv")}
          className="text-teal-600 hover:text-teal-800 hover:underline bg-transparent border-0 cursor-pointer"
        >
          CSV
        </button>
        <span>|</span>
        <button
          onClick={() => downloadTemplate("xlsx")}
          className="text-teal-600 hover:text-teal-800 hover:underline bg-transparent border-0 cursor-pointer"
        >
          Excel
        </button>
      </div>

      <ImportModal />
    </div>
  );
};

export default BulkProductImport;
